import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { cx } from "@fp/ui/utils/cx";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { EarlyDisbursementTag } from "@/components/early-disbursement-tag";
import { InvoiceExportButton } from "@/components/invoice-export-button";
import { getPayoutSourceLabelLines, PayoutSourceTags } from "@/components/payout-source";
import { describeBankAccount } from "@/components/funding/bank-accounts";
import { formatOrganizationDate } from "@/lib/organization-date";
import { getReceivableFailureCopy } from "@/lib/receivable-failure-copy";
import { formatCurrency, formatPercent, getStatusBadgeStyles } from "@/lib/payout-utils";
import axios, { getErrorMessage, type ApiErrorResponse } from "@/lib/axios";
import { toast } from "@/lib/toast";
import {
  getGetReceivableByIdQueryKey,
  getListReceivablesQueryKey,
  useElectReceivableEarlyDisbursement,
  useGetReceivableById,
} from "@fp/admin-api-client/receivables/receivables.client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import {
  DisbursementTiming,
  PayoutStatus,
  type BankAccountResponse,
  type EarlyDisbursementOfferDto,
  type ReceivableDetailDto,
  type ReceivedPaymentDto,
} from "@fp/admin-api-client";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building07 } from "@untitledui/icons";
import type { AxiosError } from "axios";
import { useState } from "react";

const OFFER_CHANGED_CODE = "EARLY_DISBURSEMENT_OFFER_CHANGED";

export const Route = createFileRoute("/funding/receivables/$payoutId")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

function describeOffer(offer: EarlyDisbursementOfferDto, receivable: ReceivableDetailDto, timeZoneId: string): string {
  const scheduledFor = receivable.releaseDate ? formatOrganizationDate(receivable.releaseDate, timeZoneId) : "a later date";
  const paysNow = new Date(offer.releaseDate).getTime() <= Date.now();
  const when = paysNow ? "now" : `on ${formatOrganizationDate(offer.releaseDate, timeZoneId)}`;

  if (offer.feeAmount > 0) {
    return `This payout is scheduled for ${scheduledFor}. Get it ${when} for a ${formatPercent(offer.feeRate)} fee of ${formatCurrency(offer.feeAmount, receivable.currency)} and receive ${formatCurrency(receivable.amount - offer.feeAmount, receivable.currency)}.`;
  }

  return `This payout is scheduled for ${scheduledFor}. Get it ${when} at no fee.`;
}

function describeSettlement(receivingAccount: BankAccountResponse | undefined): string {
  if (!receivingAccount) {
    return "Choose a receiving account on your Funding page so the payout has somewhere to land.";
  }

  const account = `Your account ending in ${receivingAccount.accountMask}`;

  return receivingAccount.isRealTimePaymentEnabled
    ? `${account} receives it instantly once it's sent. If an instant payment isn't possible, it arrives by ACH the same or next business day.`
    : `${account} receives it by ACH, typically 1-3 business days after it is sent.`;
}

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900">{value}</div>
    </div>
  );
}

function PaymentAttemptRow({ payment, payerName, currency }: { payment: ReceivedPaymentDto; payerName: string; currency: string }) {
  const failed = payment.isFailed || payment.isReturned;
  const failure = failed ? getReceivableFailureCopy(payment.failureCode, payerName) : null;

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="text-sm font-medium text-gray-900">{payment.reference}</div>
          <div className="text-xs text-gray-500">{payment.rail} · {formatLocalDate(payment.createdDate, "datetimeWithYear")}</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-gray-900">{formatCurrency(payment.allocatedAmount, currency)}</div>
          <div className={cx("text-xs font-medium", failed ? "text-red-600" : payment.settledDate ? "text-green-600" : "text-yellow-600")}>{payment.status}</div>
        </div>
      </div>
      {payment.settledDate && <div className="text-xs text-gray-500 mt-1">Settled {formatLocalDate(payment.settledDate, "datetimeWithYear")}</div>}
      {payment.returnedDate && <div className="text-xs text-gray-500 mt-1">Returned {formatLocalDate(payment.returnedDate, "datetimeWithYear")}{payment.returnCode ? ` · ${payment.returnCode}` : ""}</div>}
      {failure && <div className="text-sm text-red-700 mt-1">{failure.body}</div>}
    </div>
  );
}

function ReceivableDetail({ receivable }: { receivable: ReceivableDetailDto }) {
  const queryClient = useQueryClient();
  const payerTimeZone = receivable.payerTimeZoneId;
  const { data: capabilitiesData } = useGetOrganizationContextCapabilities();
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const { data: accounts } = useQuery({
    queryKey: ["fundingAccounts"],
    queryFn: async () => {
      const response = await axios.get<BankAccountResponse[]>("/api/admin/funding/accounts");
      return response.data;
    },
  });

  const payerName = receivable.payerOrganizationName;
  const receivingAccount = accounts?.find((account) => account.isPaymentDestination);
  const sourceLines = receivable.source ? getPayoutSourceLabelLines(receivable.source) : [];
  const earlyDisbursement = receivable.earlyDisbursement;
  const election = earlyDisbursement?.election;
  const isElected = receivable.disbursementTiming === DisbursementTiming.Early && Boolean(election);
  const offer = !isElected && receivable.status === PayoutStatus.Scheduled ? earlyDisbursement?.offer : undefined;
  const canElect = capabilitiesData?.data?.canElectReceivableEarlyDisbursement ?? false;
  const currentFailure = receivable.status !== PayoutStatus.Paid && receivable.status !== PayoutStatus.Voided
    ? [...receivable.payments].reverse().find((payment) => payment.isFailed || payment.isReturned)
    : undefined;
  const failureCopy = currentFailure ? getReceivableFailureCopy(currentFailure.failureCode, payerName) : null;

  const invalidateReceivable = async () => {
    await queryClient.invalidateQueries({ queryKey: getGetReceivableByIdQueryKey(receivable.id) });
    await queryClient.invalidateQueries({ queryKey: getListReceivablesQueryKey() });
  };

  const electMutation = useElectReceivableEarlyDisbursement<AxiosError<ApiErrorResponse>>({
    mutation: {
      onSuccess: async () => {
        setIsAcceptModalOpen(false);
        await invalidateReceivable();
        toast.success("Early disbursement accepted");
      },
      onError: async (err) => {
        setIsAcceptModalOpen(false);
        if (err.response?.data?.code === OFFER_CHANGED_CODE) {
          await invalidateReceivable();
          toast.error("The terms for getting paid early changed. Review the new fee and decide again.");
          return;
        }
        toast.error(getErrorMessage(err, "Failed to accept early disbursement"));
      },
    },
  });

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
        <div className="px-4 py-4">
          <div className="flex items-center gap-2 text-base font-semibold text-brand-600">
            <Building07 className="h-4 w-4 shrink-0" />
            {payerName}
          </div>
          {sourceLines.length > 0 && (
            <div className="mt-1">
              {sourceLines.map((line) => (
                <div key={line} className="text-sm text-gray-700">{line}</div>
              ))}
            </div>
          )}
          {receivable.source && (
            <div className="mt-2">
              <PayoutSourceTags source={receivable.source} />
            </div>
          )}
        </div>

        <div className="px-4 py-4 flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-500">Status</div>
            <div className="flex items-center gap-2 mt-1">
              <span className={cx("inline-flex items-center px-2.5 py-0.5 rounded text-sm font-medium", getStatusBadgeStyles(receivable))}>
                {receivable.status}
              </span>
              {isElected && <EarlyDisbursementTag />}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">You receive</div>
            <div className="text-2xl font-bold text-brand-600">{formatCurrency(receivable.amountOwed, receivable.currency)}</div>
          </div>
        </div>

        {isElected && earlyDisbursement && election && (
          <div className="px-4 py-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Early Disbursement</h3>
            <div className="grid grid-cols-2 gap-4">
              <DetailField label="Payout amount" value={formatCurrency(receivable.amount, receivable.currency)} />
              <DetailField label="Fee" value={`${formatPercent(earlyDisbursement.offer.feeRate)} · ${formatCurrency(earlyDisbursement.offer.feeAmount, receivable.currency)}`} />
              <DetailField label="Accepted" value={formatLocalDate(election.electedAt, "datetimeWithYear")} />
              <DetailField label="Pays" value={formatOrganizationDate(earlyDisbursement.offer.releaseDate, payerTimeZone)} />
            </div>
          </div>
        )}

        {offer && (
          <div className="px-4 py-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Get Paid Early</h3>
            <p className="text-sm text-gray-700">{describeOffer(offer, receivable, payerTimeZone)}</p>
            <p className="text-sm text-gray-500">{describeSettlement(receivingAccount)}</p>
            {canElect && (
              <Button color="primary" size="sm" onClick={() => setIsAcceptModalOpen(true)} isDisabled={!receivingAccount}>
                Accept
              </Button>
            )}
          </div>
        )}

        <div className="px-4 py-4 grid grid-cols-2 gap-4">
          <DetailField label="Created" value={formatLocalDate(receivable.createdDate, "datetimeWithYear")} />
          {receivable.releaseDate && !isElected && (
            <DetailField label="Releases" value={formatOrganizationDate(receivable.releaseDate, payerTimeZone)} />
          )}
          {receivable.paidDate && <DetailField label="Paid" value={formatLocalDate(receivable.paidDate, "datetimeWithYear")} />}
          {receivable.paymentReference && <DetailField label="Payment reference" value={receivable.paymentReference} />}
          {receivingAccount && <DetailField label="Receiving account" value={describeBankAccount(receivingAccount)} />}
        </div>

        {receivable.invoices.length > 0 && (
          <div className="px-4 py-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Invoices</h3>
            <div className="flex flex-wrap gap-2">
              {receivable.invoices.map((invoice) => (
                <InvoiceExportButton key={invoice.id} invoice={invoice} />
              ))}
            </div>
          </div>
        )}

        {failureCopy && (
          <div className="px-4 py-4">
            <h3 className="text-sm font-semibold text-red-800 mb-2">{failureCopy.title}</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-sm text-red-700">{failureCopy.body}</div>
            </div>
          </div>
        )}

        {receivable.payments.length > 0 && (
          <div className="px-4 py-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Payments</h3>
            <div className="divide-y divide-gray-200">
              {receivable.payments.map((payment) => (
                <PaymentAttemptRow key={payment.reference} payment={payment} payerName={payerName} currency={receivable.currency} />
              ))}
            </div>
          </div>
        )}
      </div>

      {offer && (
        <ConfirmationModal
          isOpen={isAcceptModalOpen}
          onClose={() => setIsAcceptModalOpen(false)}
          onConfirm={() => electMutation.mutate({ id: receivable.id, data: { releaseDate: offer.releaseDate, feeRate: offer.feeRate, feeAmount: offer.feeAmount } })}
          title="Get this payout early?"
          message={describeOffer(offer, receivable, payerTimeZone)}
          confirmText="Accept"
          isLoading={electMutation.isPending}
        />
      )}
    </>
  );
}

function RouteComponent() {
  const { payoutId } = Route.useParams();
  const { data: response, isLoading } = useGetReceivableById(payoutId);
  const receivable = response?.data;

  return (
    <AdminLayout isWide>
      <AdminPageHeader
        title="Receivable"
        parent={{ url: "/funding/receivables", label: "Receivables" }}
      />
      <PageContentContainer>
        {isLoading ? (
          <PageLoading />
        ) : receivable ? (
          <ReceivableDetail receivable={receivable} />
        ) : (
          <div className="text-center text-gray-500 py-8">This payout isn't owed to your organization</div>
        )}
      </PageContentContainer>
    </AdminLayout>
  );
}
